import * as Button from "@/shared/utils/button";
import * as Input from "@/shared/utils/input";
import {FieldError} from "@/shared/timr";

// TODO: TIMR-11610 Refactor formUtil.ts
export class FormUtil {
	private static readonly rCheckableType = (/^(?:checkbox|radio)$/i);

	/**
	 * Descendants of a container marked with `data-react-feedback` render their own validation feedback (e.g.
	 * a React panel). The jQuery feedback helpers skip them: otherwise the sweep strips the React `has-error`
	 * class off their `.form-group`, and React — seeing no prop change on the next identical render — never
	 * restores it, leaving the message without its error styling.
	 */
	private static readonly REACT_MANAGED_FEEDBACK = "[data-react-feedback] *";

	public static loading(form: JQuery) {
		const submitButton = form.find(":submit");

		Button.setLoading(submitButton);
	}

	public static resetLoading(form: JQuery) {
		const submitButton = form.find(":submit");

		Button.resetLoading(submitButton);
	}

	public static markFieldErrors(form: JQuery, fieldErrors: FieldError[], markTabTitles = false) {
		FormUtil.removeAllFeedback(form, markTabTitles);

		fieldErrors.forEach(function (fieldError) {
			const field = fieldError.field;

			const input = form.find(`input[name="${field}"], select[name="${field}"], textarea[name="${field}"], [data-field="${field}"]`)
				.not(FormUtil.REACT_MANAGED_FEEDBACK);

			Input.addFeedback(input, "has-error", fieldError.message);

			if (markTabTitles) {
				FormUtil.markTabFieldErrors(input, form);
			}
		});
	}

	/**
	 * Marks a single HTML input with an error message given a List of field errors.
	 * The first error found for the field (matched against the HTML 'name' attribute) will be used.
	 * e.g. <input name="inputFieldName" /> with fieldErrors = [{field: "inputFieldName", message: "field value has to be provided"}, {…}]
	 * will result in the error being set to "field value has to be provided". others will be ignored
	 */
	public static markFieldError(input: JQuery, fieldErrors: FieldError[]) {
		if (!input?.length || !fieldErrors) {
			return;
		}

		Input.removeFeedback(input)
		const fieldName = input.attr("name");
		const fieldError = fieldErrors.find(fieldError => fieldError.field === fieldName);
		if (fieldError) {
			Input.addFeedback(input, "has-error", fieldError.message);
		}
	}

	public static markWizardFieldErrors(form: JQuery, fieldErrors: FieldError[]) {
		FormUtil.removeAllFeedback(form);

		fieldErrors.forEach(function (fieldError) {
			const field = fieldError.field;

			const input = form.find(`input[name="${field}"], select[name="${field}"], textarea[name="${field}"]`);
			const fieldsetId = input.closest("fieldset").attr("id");
			const tab = form.find(`a[aria-controls=${fieldsetId}]`).closest("li");

			// mark up the tab (circle) of the steplist where the form error is located
			tab.switchClass("done", "error");
			Input.addFeedback(input, "has-error", fieldError.message);
		});
	}

	/**
	 * Adds the validations in a short format "<icon> Error"
	 * A field may have several warnings, these warnings are then grouped by a logic and displayed bundled in the tooltip
	 * Since there are cases where not all validations should be displayed in this short format there is a regex to specify them
	 *
	 * @param form or other jQuery element containing the inputs
	 * @param fieldErrors list of errors
	 * @param shortMessagesRegex specify which validations are displayed in the short format
	 * @param markTabTitles should the tab titles be error marked
	 */
	public static buildShortMessageFeedback(form: JQuery, fieldErrors: FieldError[], shortMessagesRegex: string, markTabTitles: boolean) {
		// mark errors by default that do not refer to the regex fields (date range)
		FormUtil.markFieldErrors(form, fieldErrors.filter(fieldError => !RegExp(shortMessagesRegex).exec(fieldError.field)), markTabTitles);

		// group all other field errors by related field
		const groupedFieldErrors: { field: string, messages: string[] }[] = [];
		fieldErrors
			.filter(fieldError => RegExp(shortMessagesRegex).exec(fieldError.field))
			.forEach(fieldError => {
				const groupIndex = groupedFieldErrors.findIndex(item => item.field === fieldError.field);
				if (groupIndex !== -1) {
					groupedFieldErrors[groupIndex].messages.push(fieldError.message);
				} else {
					groupedFieldErrors.push({
						field: fieldError.field,
						messages: [fieldError.message]
					});
				}
			});

		// add short feedback mark up for the grouped field errors (there can be multiple errors/messages)
		groupedFieldErrors.forEach(fieldError => {
			const field = form.find(`input[name="${fieldError.field}"]`);
			if (markTabTitles) {
				FormUtil.markTabFieldErrors(field, form);
			}
			Input.addShortFeedback(field, "has-error", fieldError.messages);
		});
	}

	public static removeAllFeedback(form: JQuery, unmarkTabTitles = false) {
		const input = form.find("input, select, textarea, [data-field]").not(FormUtil.REACT_MANAGED_FEEDBACK);
		Input.removeFeedback(input);

		if (unmarkTabTitles) {
			FormUtil.removeTabFeedback(form);
		}
	}

	// Checkboxes and radios can't be serialized with .val() but with .prop("checked") to get a true/false value
	// See jquery Method https://github.com/jquery/jquery/blob/main/src/serialize.js
	// Unchecked checkboxes are not serialized, defaults to false
	public static serializeArrayDisabled(form: JQuery): JQuerySerializeArrayElement[] {
		const array: JQuerySerializeArrayElement[] = [];
		form.find(":disabled[name]").each(function (this: HTMLElement) {
			const input = this as HTMLInputElement;
			const isCheckbox = FormUtil.rCheckableType.test(input.type);

			// For non-checkboxes or checked checkboxes
			if (!isCheckbox || $(this).prop("checked")) {
				array.push({name: input.name, value: $(this).val() as string});
			}
		});
		return array;
	}

	public static focusNextInput(currentInput: JQuery, skipElementSelectors: string[] = null) {
		currentInput.each(function (this: HTMLElement) {
			let fields = $(this).parents("form:eq(0),body").find("input,textarea,select").filter(":visible");
			if (skipElementSelectors !== null && skipElementSelectors.length !== 0) {
				fields = fields.not($(skipElementSelectors.join()));
			}
			const index = fields.index(this);
			if (index > -1 && (index + 1) < fields.length) {
				fields.eq(index + 1).focus();
			}
			return false;
		});
	}

	public static focusPreviousInput(currentInput: JQuery, skipElementSelectors?: string[]) {
		currentInput.each(function (this: HTMLElement) {
			let fields = $(this).parents("form:eq(0),body").find("input,textarea,select").filter(":visible");
			if (skipElementSelectors !== null && skipElementSelectors.length !== 0) {
				fields = fields.not($(skipElementSelectors.join()));
			}
			const index = fields.index(this);
			if (index > -1 && (index - 1) < fields.length) {
				fields.eq(index - 1).focus();
			}
			return false;
		});
	}

	public static readonly autoExpand = (element: HTMLElement) => {
		// Do calculation on a hidden shadow element, otherwise shadowElement.style.height = "inherit" causes flicker on IE11
		const shadow = $(element).clone();
		shadow.css({
			position: "absolute",
			top: -9999,
			left: -9999
		});
		shadow.appendTo($(element).parent());
		const shadowElement = shadow[0];
		shadowElement.style.height = "inherit";
		const computed = window.getComputedStyle(shadowElement);
		const height = parseInt(computed.getPropertyValue("border-top-width"), 10)
			+ parseInt(computed.getPropertyValue("padding-top"), 10)
			+ shadowElement.scrollHeight
			+ parseInt(computed.getPropertyValue("padding-bottom"), 10)
			+ parseInt(computed.getPropertyValue("border-bottom-width"), 10) - 10;
		shadow.remove();
		element.style.height = height + "px";
	}

	public static switchTabNavigationToFirst(navTabs: JQuery) {
		const firstTab = navTabs.find("li.nav-item:not(.disabled) > a.nav-link:first");
		firstTab.tab("show");
	}

	public static markTabFieldErrors(input: JQuery, form: JQuery) {
		const id = input.closest(".tab-pane")?.get(0)?.id;

		if (id) {
			const element = form.find("ul.nav-tabs").find(`a[href="#${id}"]`);
			element.closest("li").addClass("has-error");

			if (element.children().length === 0) {
				element.prepend(`<i class="far fa-exclamation-triangle position-left"></i>`);
			}
		}
	}

	/**
	 * Removes the error mark from the tab title
	 * If input is provided, only the tab of the input is unmarked
	 * @param form the form containing the tabs
	 * @param input optional input field to unmark
	 */
	public static removeTabFeedback(form: JQuery, input?: JQuery) {
		if (input) {
			const id = input.closest(".tab-pane")?.attr("id");
			if (id) {
				const element = form.find(`ul.nav-tabs a[href="#${id}"]`).closest("li");
				element.removeClass("has-error").find("i").remove();
			}
		} else {
			form.find("ul.nav-tabs li.nav-item.has-error").each((_, elem) => {
				$(elem).removeClass("has-error").find("i").remove();
			});
		}
	}

	public static hasFeedback(form: JQuery): boolean {
		const input = form.find("input, select, textarea");
		const formGroup = input.closest(".form-group, .form-group-custom-field");

		return formGroup.hasClass("has-feedback");
	}

	/**
	 * Checks if the tab of the input has feedback
	 * @param input
	 * @param form
	 */
	public static hasTabFeedback(input: JQuery, form: JQuery): boolean {
		const id = input.closest(".tab-pane")?.get(0)?.id;

		if (id) {
			const element = form.find(`#${id}`);
			const formGroup = element?.find(".form-group, .form-group-custom-field");

			return formGroup?.hasClass("has-feedback");
		}
		return false;
	}
}
